import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Plus, Check, Settings, Users, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useWorkspaceStore, Workspace } from '../../store/workspaceStore';
import { getErrorMessage } from '../../services/api';

const WorkspaceSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    workspaces,
    activeWorkspaceId,
    isLoading,
    fetchWorkspaces,
    switchWorkspace,
    createWorkspace,
  } = useWorkspaceStore();

  const [isOpen, setIsOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#2383e2');
  const [newDesc, setNewDesc] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isSwitching, setIsSwitching] = useState<string | null>(null);

  // Accessibility IDs
  const nameId = React.useId();
  const descId = React.useId();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) || workspaces.find((w) => w.isActive);
  const colorPresets = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444'];

  // Fetch workspaces on mount
  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setShowCreateModal(false);
      }
    };
    if (isOpen || showCreateModal) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [isOpen, showCreateModal]);

  const handleSwitch = async (ws: Workspace) => {
    if (ws.id === activeWorkspaceId) {
      setIsOpen(false);
      return;
    }
    setIsSwitching(ws.id);
    try {
      await switchWorkspace(ws.id);
      setIsOpen(false);
      toast.success(t('ws_switch_switched', 'Switched to {{name}}', { name: ws.name }));
      // Reload data for the new workspace context
      window.location.reload();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, t('ws_switch_failed', 'Failed to switch workspace')));
    } finally {
      setIsSwitching(null);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setIsCreating(true);
    try {
      await createWorkspace({
        name: newName.trim(),
        description: newDesc.trim() || undefined,
        color: newColor,
      });
      setNewName('');
      setNewColor('#2383e2');
      setNewDesc('');
      setShowCreateModal(false);
      setIsOpen(false);
      toast.success(t('ws_switch_created', 'Workspace created'));
      window.location.reload();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, t('ws_switch_create_failed', 'Failed to create workspace')));
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading && workspaces.length === 0) {
    return (
      <div className="w-full flex items-center gap-2.5 px-2.5 py-2">
        <div className="w-7 h-7 rounded-md bg-n-border animate-pulse" />
        <div className="flex-1">
          <div className="h-3.5 w-24 bg-n-border rounded animate-pulse" />
          <div className="h-2.5 w-16 bg-n-hover rounded animate-pulse mt-1" />
        </div>
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => { setIsOpen(!isOpen); setShowCreateModal(false); }}
        className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-n-sm transition ${
          isOpen ? 'bg-n-blue-bg' : 'hover:bg-n-hover'
        }`}
      >
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
          style={{ backgroundColor: currentWorkspace?.color || '#2383e2' }}
        >
          {currentWorkspace?.name?.charAt(0)?.toUpperCase() || 'W'}
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className="text-sm font-semibold text-n-text truncate leading-tight">
            {currentWorkspace?.name || t('ws_switch_workspace_default', 'Workspace')}
          </p>
          <p className="text-[11px] text-n-text-lt leading-tight">
            {currentWorkspace?.memberCount || 1} {t('ws_switch_member', 'member', { count: (currentWorkspace?.memberCount || 1) })}
            {(currentWorkspace?.memberCount || 1) !== 1 ? 's' : ''}
          </p>
        </div>
        <ChevronDown
          size={14}
          className={`text-n-text-lt transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && !showCreateModal && (
        <div className="n-popover left-0 top-full mt-1.5 w-[260px] z-50 overflow-hidden"
        >
          {/* Workspace List */}
          <div className="py-1.5 max-h-64 overflow-y-auto">
            {workspaces.map((ws) => {
              const isActive = ws.id === activeWorkspaceId;
              const switching = isSwitching === ws.id;
              return (
                <button
                  key={ws.id}
                  onClick={() => handleSwitch(ws)}
                  disabled={switching}
                  className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 transition text-left ${
                    isActive ? 'bg-n-hover' : 'hover:bg-n-hover'
                  }`}
                >
                  <div
                    className="w-8 h-8 rounded-md flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                    style={{ backgroundColor: ws.color }}
                  >
                    {switching ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      ws.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-n-text truncate">{ws.name}</p>
                    {ws.description && (
                      <p className="text-[11px] text-n-text-lt truncate">{ws.description}</p>
                    )}
                    <div className="flex items-center gap-1 mt-0.5">
                      <Users size={10} className="text-n-text-lt" />
                      <span className="text-[11px] text-n-text-lt">{ws.memberCount}</span>
                    </div>
                  </div>
                  {isActive && (
                    <Check size={16} className="text-n-blue flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Actions */}
          <div className="border-t border-n-border">
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-n-text-md hover:bg-n-hover transition text-left"
            >
              <Plus size={15} className="text-n-text-lt" />
              <span className="text-sm">{t('ws_switch_create_workspace_button', 'Create Workspace')}</span>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setIsOpen(false); navigate('/team'); }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-n-text-md hover:bg-n-hover transition text-left"
            >
              <Settings size={15} className="text-n-text-lt" />
              <span className="text-sm">{t('ws_switch_manage_workspaces', 'Manage Workspaces')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="n-popover left-0 top-full mt-1.5 w-[280px] z-50 p-4"
        >
          <h3 className="text-sm font-bold text-n-text mb-3">{t('ws_switch_create_workspace_modal_title', 'Create Workspace')}</h3>

          <div className="mb-3">
            <label htmlFor={nameId} className="n-label mb-1">{t('ws_switch_name_label', 'Name')}</label>
            <input
              id={nameId}
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t('ws_switch_name_placeholder', 'Workspace name')}
              className="n-input"
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
          </div>

          <div className="mb-3">
            <span className="n-label mb-1">{t('ws_switch_color_label', 'Color')}</span>
            <div className="flex gap-2">
              {colorPresets.map((c) => (
                <button
                  key={c}
                  onClick={() => setNewColor(c)}
                  className="w-7 h-7 rounded-full transition-all hover:scale-110"
                  style={{
                    backgroundColor: c,
                    boxShadow: newColor === c ? `0 0 0 2px white, 0 0 0 4px ${c}` : 'none',
                  }}
                />
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor={descId} className="n-label mb-1">{t('ws_switch_description_label', 'Description (optional)')}</label>
            <input
              id={descId}
              type="text"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder={t('ws_switch_description_placeholder', 'Short description')}
              className="n-input"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => { setShowCreateModal(false); setNewName(''); setNewColor('#2383e2'); setNewDesc(''); }}
              className="n-btn n-btn-secondary flex-1"
            >
              {t('ws_switch_cancel_button', 'Cancel')}
            </button>
            <button
              onClick={handleCreate}
              disabled={!newName.trim() || isCreating}
              className="n-btn n-btn-primary flex-1 flex items-center justify-center gap-1.5"
            >
              {isCreating ? <Loader2 size={14} className="animate-spin" /> : null}
              {isCreating ? t('ws_switch_creating', 'Creating...') : t('ws_switch_create_button', 'Create')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspaceSwitcher;
